'use client';

import { useCallback, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from '@/app_components/common/Link';
import { Pagination } from '@/app_components/common/Pagination';
import { InfoItem } from '@/app_components/widgets/RatesWidget/InfoItem';
import { RatesCard } from '@/app_components/widgets/RatesWidget/RatesCard';
import { RatesInput } from '@/app_components/widgets/RatesWidget/RatesInput';
import { TopSection } from '@/app_components/widgets/RatesWidget/TopSection';
import { BankrateLogo } from '@/app_components/icons/BankrateLogo';
import {
  defaultRatesFormValues,
  type RatesApiParameters,
  type RatesTypes,
  RatesWidgetMapper,
  ratesInfoOutput,
} from '@/app_lib/utils/widgets/redVenturesConstants';
import type { FilterBy, RatesData } from '@/app_data/widgets/utils';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { useWidgetsIndex } from '@/app_lib/hooks/useWidgetsIndex';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { trackEvent } from '@/app_lib/services/Gtm/utils';

const getPageCount = (recordCount: number, pageSize: number) => {
  if (recordCount === 0) {
    return 1;
  }
  return Math.ceil(recordCount / pageSize);
};

const paginate = (array: RatesData[], page: number, pageSize: number) => {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return array.slice(start, end);
};

interface RatesWidgetProps {
  className?: string;
  userZipCode?: string;
  type: RatesTypes;
  initialData?: RatesData[];
  postId: number;
}

const depositAmountDefault = 25000;
const mobilePageSize = 2;
const desktopPageSize = 3;

export const RatesWidget = ({ initialData = [], type, userZipCode, postId }: RatesWidgetProps) => {
  const { pageData } = useContext(GtmContext);

  const [page, setPage] = useState(1);
  const widgetIndex = useWidgetsIndex();

  const [depositAmount, setDepositAmount] = useState(depositAmountDefault);
  const [rates, setRates] = useState<RatesData[]>(initialData);
  const [fetched, setFetched] = useState(Boolean(initialData.length));

  const mobileQuery = useMediaQuery(Breakpoints.Mobile);

  const pageSize = mobileQuery ? mobilePageSize : desktopPageSize;

  const title = RatesWidgetMapper[type].title;
  const inputs = RatesWidgetMapper[type].inputs;
  const outputs = RatesWidgetMapper[type].outputs;
  const getDataFunc = RatesWidgetMapper[type].getDataFunc;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <only run on mount>
  useEffect(() => {
    async function fetchInitialData() {
      return await getDataFunc(defaultRatesFormValues);
    }

    if (!initialData.length) {
      fetchInitialData().then((data) => {
        setRates(data);
        setFetched(true);
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: helps with development so we don't have to refresh the page
  useEffect(() => {
    setPage(1);
  }, [mobileQuery]);

  useEffect(() => {
    // TODO: Is this outdated? Shouldn't this just be an impression-element with data-content-placement?
    trackEvent({
      ...pageData,
      // biome-ignore lint/style/useNamingConvention: <external data>
      contentPlacementCD: 'recommends widget',
      eventAction: 'impression',
      eventCategory: 'recommends widget',
      eventLabel: `${type.toLowerCase()} - ${widgetIndex}`,
    });
  }, [type, widgetIndex, pageData]);

  const fetchData = useCallback(
    async ({
      depositAmount: depositInput,
      page: pageInput,
      pageSize: pageSizeInput,
      minTermLength,
      maxTermLength,
      zip,
      accountType,
    }: RatesApiParameters) => {
      try {
        const data = await getDataFunc({
          accountType,
          depositAmount: depositInput,
          maxTermLength,
          minTermLength,
          page: pageInput,
          pageSize: pageSizeInput,
          zip,
        });
        setRates(data.sort((dataA, dataB) => (dataB.is_paid ? 1 : 0) - (dataA.is_paid ? 1 : 0)));
        if (depositInput !== depositAmount) {
          setDepositAmount(depositInput || 0);
        }
      } catch {
        // TODO: Add logging
      } finally {
        setPage(1);
        setFetched(true);
      }
    },
    [depositAmount, getDataFunc],
  );

  const handleOnInputChange = (filters: FilterBy) => {
    fetchData(filters);
  };

  return (
    <div className='flex flex-col pb-4 gap-4 items-start border-1 stroke-secondary bg-pure'>
      {/* Header */}
      <TopSection title={title} />

      {/* Form */}
      <RatesInput
        inputs={inputs}
        onChange={handleOnInputChange}
        userZipCode={userZipCode}
      />

      {/* Bank Cards Container */}
      <div className='flex flex-col items-start gap-4 self-stretch'>
        {/* Data */}
        <div className='hidden md:flex items-start self-stretch justify-between gap-4 py-0 px-4'>
          {outputs.map((output) => {
            const outputItem = ratesInfoOutput[output];
            return (
              <InfoItem
                key={`rates_${output}`}
                title={outputItem.title}
                tooltip={outputItem.tooltip?.replace('$DEPOSIT_AMOUNT$', depositAmount.toString())}
              />
            );
          })}
        </div>

        {/* Bank Cards */}
        <ul className='flex flex-col items-start self-stretch'>
          {rates.length === 0 && !fetched
            ? Array.from({ length: pageSize }).map(() => (
                <li
                  className='odd:bg-helper w-full h-[250px] animate-pulse'
                  key={uuidv4()}
                />
              ))
            : paginate(rates, page, pageSize).map((cdRate, index) => (
                <li
                  className='odd:bg-helper w-full'
                  key={`rates_${cdRate.ep_url}${index}`}
                >
                  <RatesCard
                    data={cdRate}
                    depositAmount={depositAmount}
                    outputs={outputs}
                    postId={postId}
                  />
                </li>
              ))}
        </ul>
        {rates.length === 0 && fetched && (
          <div className='flex items-center justify-center w-full h-[200px]'>
            <span className='typography-xs text-secondary'>No results found</span>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className='flex justify-center w-full'>
        <Pagination
          asButton
          currentPage={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalPages={getPageCount(rates.length, pageSize)}
        />
      </div>
      <Link
        className='flex self-center items-center gap-2 md:hidden'
        href='https://www.bankrate.com/'
        rel='nofollow noopener noreferrer'
        target='_blank'
      >
        <span className='typography-xs hover:underline text-secondary'>Powered by</span>
        <BankrateLogo className='text-primary' />
      </Link>
    </div>
  );
};
